import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { FaMapMarkedAlt } from "react-icons/fa";
import {
  FaEye,
  FaIndustry,
  FaCogs,
  FaMicrochip,
  FaTools,
  FaCubes,
  FaGlobe,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaBuilding,
  FaIdCard,
  FaCity,
  FaCheckCircle,
} from "react-icons/fa";
import {
  Card,
  Row,
  Col,
  Form,
  Button,
  Table,
  Spinner,
  Nav,
} from "react-bootstrap";
import { Link, useLocation, useParams } from "react-router-dom";
import Select from "react-select";

const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:5002";
const DEFAULT_USER_IMAGE = "/asset/images/default-user-aida.png";

const FONT_STACK =
  '"Inter", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const COLORS = {
  pageBg: "#ffffff",
  cardBg: "#ffffff",
  border: "#edf1f5",
  borderStrong: "#909ba8",
  textPrimary: "#0f172a",
  textSecondary: "#64748b",
  textMuted: "#94a3b8",
  brand: "#facc15",
  brandDark: "#eab308",
  brandSoft: "#fff8db",
  successBg: "#dcfce7",
  successText: "#15803d",
  successBorder: "#bbf7d0",
  shadow: "0 10px 28px rgba(15, 23, 42, 0.05)",
  softShadow: "0 4px 16px rgba(15, 23, 42, 0.04)",
};

const sectionBlockStyle: React.CSSProperties = {
  border: "1px solid #cfd8e3", // darker border
  borderRadius: 16,
  background: "#fff",
  padding: "20px",
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: "24px",
  fontWeight: 600,
  color: COLORS.textPrimary,
  marginBottom: 0,
  letterSpacing: "-0.02em",
  fontFamily: FONT_STACK,
};

const smallLabelStyle: React.CSSProperties = {
  color: COLORS.textSecondary,
  fontSize: 13,
  fontWeight: 600,
  marginBottom: 6,
  fontFamily: FONT_STACK,
};

const shellCardStyle: React.CSSProperties = {
  background: "#ffffff",
  border: `1px solid ${COLORS.border}`,
  borderRadius: 24,
  boxShadow: COLORS.shadow,
};

const infoCardStyle: React.CSSProperties = {
  background: "#ffffff",
  border: `1px solid ${COLORS.border}`,
  borderRadius: 20,
  padding: "18px 18px",
  minHeight: 98,
  boxShadow: "0 2px 8px rgba(15, 23, 42, 0.03)",
};

const tableWrapStyle: React.CSSProperties = {
  border: `1px solid ${COLORS.border}`,
  borderRadius: 16,
  overflow: "hidden",
  background: "#ffffff",
};

const displayValue = (value: any) => {
  if (
    value === null ||
    value === undefined ||
    value === "" ||
    value === "null"
  ) {
    return "-";
  }
  return value;
};

const cleanNullableString = (value: any) => {
  if (
    value === null ||
    value === undefined ||
    value === "" ||
    value === "null" ||
    value === "NULL"
  ) {
    return "";
  }
  return value;
};

const sanitizeDigitsOnly = (value: any) =>
  String(value ?? "").replace(/\D/g, "");

const sanitizeLettersOnly = (value: any) =>
  String(value ?? "").replace(/[^a-zA-ZÀ-ÿ\s'-]/g, "");

const sanitizeNameWithNumbers = (value: any) =>
  String(value ?? "").replace(/[^a-zA-ZÀ-ÿ0-9\s'-]/g, "");

const validateEmail = (value: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const validateWebsite = (value: string) =>
  /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/.*)?$/i.test(value);

interface SupplierProfile {
  legal_name: string;
  trade_name: string;
  rc_number: string;
  nif: string;
  nis: string;
  address: string;
  description: string;
  wilaya: string;
  website: string;
  workforce_total: string;
  industrial_status: string;
  country: string;
  logo?: string;
  email?: string;
  phone?: string;
}

interface Product {
  id_product: number;
  product_name: string;
  category: string;
  material: string;
  annual_capacity: number;
  unit: string;
}

interface Machine {
  id_machine: number;
  machine_name: string;
  machine_type: string;
  manufacturer: string;
  capacity: string;
  quantity: number;
}

interface Certification {
  id: number;
  certification_name: string;
  certificate_number?: string;
  issued_date?: string;
  expiry_date?: string;
}

interface SupplierDocument {
  id_document: number;
  document_type: string;
  file_path: string;
}

interface SupplierActivityItem {
  id_rfq: number;
  title: string;
  description: string;
  deadline?: string;
  rfq_status: string;
  created_at: string;
  segment_name?: string;
  viewed_at?: string;
  id_response?: number;
  response_status?: string;
  response_created_at?: string;
  shortlisted_at?: string;
  awarded_at?: string;
  score?: number;
  match_status?: string;
  id_match?: number;
}

interface SupplierCapability {
  id_segment: number;
  name: string;
}

type ProfileField =
  | "legal_name"
  | "trade_name"
  | "nif"
  | "nis"
  | "rc_number"
  | "workforce_total"
  | "website"
  | "email"
  | "country"
  | "wilaya"
  | "phone"
  | "address"
  | "description"
  | "industrial_status";

type FieldErrors = Partial<Record<ProfileField, string>>;

const professionalMessages = {
  text: "Please enter letters only.",
  number: "Please enter numbers only.",
  email: "Please enter a valid email address.",
  website: "Please enter a valid website URL.",
  requiredWilaya: "Please select a wilaya from the list.",
};

const normalizeProfileData = (data: any): SupplierProfile => ({
  legal_name: cleanNullableString(data?.legal_name),
  trade_name: cleanNullableString(data?.trade_name),
  rc_number: sanitizeDigitsOnly(cleanNullableString(data?.rc_number)),
  nif: sanitizeDigitsOnly(cleanNullableString(data?.nif)),
  nis: sanitizeDigitsOnly(cleanNullableString(data?.nis)),
  address: cleanNullableString(data?.address),
  description: cleanNullableString(data?.description),
  wilaya: cleanNullableString(data?.wilaya),
  website: cleanNullableString(data?.website),
  workforce_total: sanitizeDigitsOnly(
    cleanNullableString(data?.workforce_total),
  ),
  industrial_status: cleanNullableString(data?.industrial_status),
  country: cleanNullableString(data?.country),
  logo: cleanNullableString(data?.logo),
  email: cleanNullableString(data?.email),
  phone: sanitizeDigitsOnly(cleanNullableString(data?.phone)),
});

const formatDate = (date?: string) => {
  if (!date) return "-";
  return new Date(date).toLocaleDateString();
};

function InfoCard({
  label,
  value,
  icon,
  iconBg = COLORS.brandSoft,
  iconColor = "#d4a106",
}: {
  label: string;
  value: React.ReactNode;
  icon?: React.ReactNode;
  iconBg?: string;
  iconColor?: string;
}) {
  return (
    <Card className="border-2" style={infoCardStyle}>
      <div className="d-flex align-items-start gap-3">
        {icon && (
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 14,
              background: iconBg,
              color: iconColor,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              fontSize: 15,
            }}
          >
            {icon}
          </div>
        )}
        <div style={{ flex: 1 }}>
          <div style={{ ...smallLabelStyle, marginBottom: 4 }}>{label}</div>
          <div
            style={{
              color: COLORS.textPrimary,
              fontSize: 16,
              fontWeight: 600,
              lineHeight: 1.4,
              wordBreak: "break-word",
              fontFamily: FONT_STACK,
            }}
          >
            {value}
          </div>
        </div>
      </div>
    </Card>
  );
}

function ContactMiniCard({
  label,
  value,
  href,
}: {
  label: string;
  value: React.ReactNode;
  href?: string;
}) {
  return (
    <Card className="border-0 h-100" style={infoCardStyle}>
      <div style={smallLabelStyle}>{label}</div>
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          style={{
            color: COLORS.textPrimary,
            textDecoration: "none",
            fontWeight: 700,
            lineHeight: 1.5,
            wordBreak: "break-word",
            fontFamily: FONT_STACK,
          }}
        >
          {value}
        </a>
      ) : (
        <div
          style={{
            color: COLORS.textPrimary,
            fontWeight: 700,
            lineHeight: 1.5,
            wordBreak: "break-word",
            fontFamily: FONT_STACK,
          }}
        >
          {value}
        </div>
      )}
    </Card>
  );
}

function ContactProCard({
  email,
  phone,
  website,
}: {
  email?: string;
  phone?: string;
  website?: string;
}) {
  const safeWebsite =
    website && website !== "-"
      ? String(website).startsWith("http")
        ? String(website)
        : `https://${String(website)}`
      : undefined;

  const rows = [
    {
      label: "Email",
      value: email || "-",
      icon: <FaEnvelope />,
      href: email && email !== "-" ? `mailto:${email}` : undefined,
      iconBg: "#eef4ff",
      iconColor: "#315efb",
    },
    {
      label: "Phone",
      value: phone || "-",
      icon: <FaPhone />,
      href: phone && phone !== "-" ? `tel:${phone}` : undefined,
      iconBg: "#fff4e6",
      iconColor: "#f59e0b",
    },
    {
      label: "Website",
      value: website || "-",
      icon: <FaGlobe />,
      href: safeWebsite,
      iconBg: "#ecfdf3",
      iconColor: "#16a34a",
    },
  ];

  return (
    <Card
      className="border-0 mt-4"
      style={{
        ...shellCardStyle,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          height: 5,
          background: "linear-gradient(90deg, #f4c400 0%, #ffd54a 100%)",
        }}
      />

      <Card.Body className="p-4">
        <div className="d-flex align-items-center gap-2 mb-3">
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 12,
              background: "#fff8db",
              color: "#d4a106",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <FaPhone />
          </div>

          <div>
            <h4
              style={{
                ...sectionTitleStyle,
                fontSize: 22,
                marginBottom: 2,
              }}
            >
              Contact
            </h4>
            <div
              style={{
                fontSize: 13,
                color: COLORS.textSecondary,
                fontWeight: 500,
                fontFamily: FONT_STACK,
              }}
            >
              Direct company contact details
            </div>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 14,
          }}
        >
          {rows.map((item) => (
            <div
              key={item.label}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 14,
                padding: "14px 14px",
                border: `1px solid ${COLORS.border}`,
                borderRadius: 18,
                background: "#ffffff",
                boxShadow: "0 8px 20px rgba(15, 23, 42, 0.05)",
              }}
            >
              <div
                style={{
                  width: 46,
                  height: 46,
                  borderRadius: 14,
                  background: item.iconBg,
                  color: item.iconColor,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  fontSize: 16,
                }}
              >
                {item.icon}
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: COLORS.textSecondary,
                    marginBottom: 3,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    fontFamily: FONT_STACK,
                  }}
                >
                  {item.label}
                </div>

                {item.href ? (
                  <a
                    href={item.href}
                    target={item.label === "Website" ? "_blank" : undefined}
                    rel={item.label === "Website" ? "noreferrer" : undefined}
                    style={{
                      color: COLORS.textPrimary,
                      textDecoration: "none",
                      fontWeight: 700,
                      lineHeight: 1.45,
                      wordBreak: "break-word",
                      fontFamily: FONT_STACK,
                      fontSize: 15,
                    }}
                  >
                    {item.value}
                  </a>
                ) : (
                  <div
                    style={{
                      color: COLORS.textPrimary,
                      fontWeight: 700,
                      lineHeight: 1.45,
                      wordBreak: "break-word",
                      fontFamily: FONT_STACK,
                      fontSize: 15,
                    }}
                  >
                    {item.value}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card.Body>
    </Card>
  );
}

function MetricCard({
  label,
  value,
}: {
  label: string;
  value: number | string;
}) {
  return (
    <div
      className="text-center"
      style={{
        background: "#ffffff",
        border: `1px solid ${COLORS.border}`,
        borderRadius: 18,
        padding: "16px 10px",
        boxShadow: COLORS.softShadow,
      }}
    >
      <div
        style={{
          fontSize: 30,
          fontWeight: 600,
          color: COLORS.textPrimary,
          lineHeight: 1.1,
          marginBottom: 6,
          fontFamily: FONT_STACK,
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: 13,
          color: COLORS.textSecondary,
          fontWeight: 700,
          fontFamily: FONT_STACK,
        }}
      >
        {label}
      </div>
    </div>
  );
}

export default function ProfileSupplier() {
  const location = useLocation();
  const { id_user: routeUserId } = useParams();
  const loggedInUserId = localStorage.getItem("userid") || "1";
  const id_user = routeUserId || loggedInUserId;
  const isViewingOwnProfile = id_user === loggedInUserId;
  const canManageSupplierProfile =
    isViewingOwnProfile || location.state?.canEditCompany === true;

  const [activeTab, setActiveTab] = useState<
    | "overview"
    | "company"
    | "products"
    | "machines"
    | "certifications"
    | "documents"
    | "activity"
  >("overview");

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(true);
  const [allSegments, setAllSegments] = useState<SupplierCapability[]>([]);
  const [selectedCapabilities, setSelectedCapabilities] = useState<number[]>(
    [],
  );
  const [isEditingCapabilities, setIsEditingCapabilities] = useState(false);

  const [profile, setProfile] = useState<SupplierProfile>({
    legal_name: "",
    trade_name: "",
    rc_number: "",
    nif: "",
    nis: "",
    address: "",
    description: "",
    wilaya: "",
    website: "",
    workforce_total: "",
    industrial_status: "",
    country: "",
    email: "",
    phone: "",
  });

  const [isEditing, setIsEditing] = useState(false);
  const [tempProfile, setTempProfile] = useState<SupplierProfile>(profile);
  const [products, setProducts] = useState<Product[]>([]);
  const [machines, setMachines] = useState<Machine[]>([]);
  const [certifications, setCertifications] = useState<Certification[]>([]);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [capabilities, setCapabilities] = useState<SupplierCapability[]>([]);
  const [documents, setDocuments] = useState<SupplierDocument[]>([]);
  const [activity, setActivity] = useState<SupplierActivityItem[]>([]);
  const [stats, setStats] = useState({
    products: 0,
    machines: 0,
    certifications: 0,
  });
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const wilayaOptions = [
    { value: "Adrar", label: "01 - Adrar" },
    { value: "Chlef", label: "02 - Chlef" },
    { value: "Laghouat", label: "03 - Laghouat" },
    { value: "Oum El Bouaghi", label: "04 - Oum El Bouaghi" },
    { value: "Batna", label: "05 - Batna" },
    { value: "Bejaia", label: "06 - Bejaia" },
    { value: "Biskra", label: "07 - Biskra" },
    { value: "Bechar", label: "08 - Bechar" },
    { value: "Blida", label: "09 - Blida" },
    { value: "Bouira", label: "10 - Bouira" },
    { value: "Tamanrasset", label: "11 - Tamanrasset" },
    { value: "Tebessa", label: "12 - Tebessa" },
    { value: "Tlemcen", label: "13 - Tlemcen" },
    { value: "Tiaret", label: "14 - Tiaret" },
    { value: "Tizi Ouzou", label: "15 - Tizi Ouzou" },
    { value: "Alger", label: "16 - Alger" },
    { value: "Djelfa", label: "17 - Djelfa" },
    { value: "Jijel", label: "18 - Jijel" },
    { value: "Setif", label: "19 - Setif" },
    { value: "Saida", label: "20 - Saida" },
    { value: "Skikda", label: "21 - Skikda" },
    { value: "Sidi Bel Abbes", label: "22 - Sidi Bel Abbes" },
    { value: "Annaba", label: "23 - Annaba" },
    { value: "Guelma", label: "24 - Guelma" },
    { value: "Constantine", label: "25 - Constantine" },
    { value: "Medea", label: "26 - Medea" },
    { value: "Mostaganem", label: "27 - Mostaganem" },
    { value: "M'Sila", label: "28 - M'Sila" },
    { value: "Mascara", label: "29 - Mascara" },
    { value: "Ouargla", label: "30 - Ouargla" },
    { value: "Oran", label: "31 - Oran" },
    { value: "El Bayadh", label: "32 - El Bayadh" },
    { value: "Illizi", label: "33 - Illizi" },
    { value: "Bordj Bou Arreridj", label: "34 - Bordj Bou Arreridj" },
    { value: "Boumerdes", label: "35 - Boumerdes" },
    { value: "El Tarf", label: "36 - El Tarf" },
    { value: "Tindouf", label: "37 - Tindouf" },
    { value: "Tissemsilt", label: "38 - Tissemsilt" },
    { value: "El Oued", label: "39 - El Oued" },
    { value: "Khenchela", label: "40 - Khenchela" },
    { value: "Souk Ahras", label: "41 - Souk Ahras" },
    { value: "Tipaza", label: "42 - Tipaza" },
    { value: "Mila", label: "43 - Mila" },
    { value: "Ain Defla", label: "44 - Ain Defla" },
    { value: "Naama", label: "45 - Naama" },
    { value: "Ain Temouchent", label: "46 - Ain Temouchent" },
    { value: "Ghardaia", label: "47 - Ghardaia" },
    { value: "Relizane", label: "48 - Relizane" },
    { value: "Timimoun", label: "49 - Timimoun" },
    { value: "Bordj Badji Mokhtar", label: "50 - Bordj Badji Mokhtar" },
    { value: "Ouled Djellal", label: "51 - Ouled Djellal" },
    { value: "Beni Abbes", label: "52 - Beni Abbes" },
    { value: "In Salah", label: "53 - In Salah" },
    { value: "In Guezzam", label: "54 - In Guezzam" },
    { value: "Touggourt", label: "55 - Touggourt" },
    { value: "Djanet", label: "56 - Djanet" },
    { value: "El M'Ghair", label: "57 - El M'Ghair" },
    { value: "El Meniaa", label: "58 - El Meniaa" },
  ];

  const getStatusBadge = (status: string) => {
    const value = (status || "incomplete").toLowerCase();

    const config = {
      active: {
        label: "Active",
        bg: "linear-gradient(135deg, #ecfdf3 0%, #d1fae5 100%)",
        color: "#166534",
        border: "#86efac",
        dot: "#22c55e",
      },
      inactive: {
        label: "Inactive",
        bg: "linear-gradient(135deg, #fff1f2 0%, #ffe4e6 100%)",
        color: "#b91c1c",
        border: "#fda4af",
        dot: "#ef4444",
      },
      incomplete: {
        label: "Incomplete",
        bg: "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)",
        color: "#b45309",
        border: "#fcd34d",
        dot: "#f59e0b",
      },
    };

    const current = config[value as keyof typeof config] || config.incomplete;

    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          minWidth: 116,
          padding: "9px 16px",
          borderRadius: 999,
          background: current.bg,
          color: current.color,
          border: `1px solid ${current.border}`,
          fontSize: 13,
          fontWeight: 700,
          lineHeight: 1,
          fontFamily: FONT_STACK,
        }}
      >
        <span
          style={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            background: current.dot,
            display: "inline-block",
            flexShrink: 0,
            boxShadow: `0 0 0 4px ${current.dot}22`,
          }}
        />
        {current.label}
      </span>
    );
  };

  const setFieldValue = (field: ProfileField, value: string) => {
    setTempProfile((prev) => ({ ...prev, [field]: value }));
  };

  const clearFieldError = (field: ProfileField) => {
    setFieldErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleTextFieldChange = (field: ProfileField, value: string) => {
    const fieldsAllowingNumbers: ProfileField[] = ["legal_name", "trade_name"];

    const cleaned = fieldsAllowingNumbers.includes(field)
      ? sanitizeNameWithNumbers(value)
      : sanitizeLettersOnly(value);

    setFieldValue(field, cleaned);

    if (value !== cleaned) {
      setFieldErrors((prev) => ({
        ...prev,
        [field]: fieldsAllowingNumbers.includes(field)
          ? "This field can contain letters and numbers only, and must include at least one letter."
          : professionalMessages.text,
      }));
    } else {
      clearFieldError(field);
    }
  };

  const handleNumberFieldChange = (field: ProfileField, value: string) => {
    const cleaned = sanitizeDigitsOnly(value);
    setFieldValue(field, cleaned);

    if (value !== cleaned) {
      setFieldErrors((prev) => ({
        ...prev,
        [field]: professionalMessages.number,
      }));
    } else {
      clearFieldError(field);
    }
  };

  const handleEmailChange = (value: string) => {
    setFieldValue("email", value);
    if (!value) return clearFieldError("email");

    setFieldErrors((prev) => ({
      ...prev,
      email: validateEmail(value) ? "" : professionalMessages.email,
    }));
  };

  const handleWebsiteChange = (value: string) => {
    setFieldValue("website", value);
    if (!value) return clearFieldError("website");

    setFieldErrors((prev) => ({
      ...prev,
      website: validateWebsite(value) ? "" : professionalMessages.website,
    }));
  };

  const handleWilayaChange = (
    selected: { value: string; label: string } | null,
  ) => {
    setFieldValue("wilaya", selected?.value || "");
    setFieldErrors((prev) => ({
      ...prev,
      wilaya: selected?.value ? "" : professionalMessages.requiredWilaya,
    }));
  };

  const validateFields = () => {
    const errors: FieldErrors = {};

    if (!tempProfile.legal_name || tempProfile.legal_name.trim() === "") {
      errors.legal_name = "Company name is required.";
    } else if (!/^(?=.*[a-zA-Z])[a-zA-Z0-9\s]+$/.test(tempProfile.legal_name)) {
      errors.legal_name =
        "Company name must contain at least one letter and can include numbers.";
    }

    if (!tempProfile.trade_name || tempProfile.trade_name.trim() === "") {
      errors.trade_name = "Trade name is required.";
    } else if (!/^(?=.*[a-zA-Z])[a-zA-Z0-9\s]+$/.test(tempProfile.trade_name)) {
      errors.trade_name =
        "Trade name must contain at least one letter and can include numbers.";
    }

    if (
      tempProfile.country &&
      sanitizeLettersOnly(tempProfile.country) !== tempProfile.country
    ) {
      errors.country = professionalMessages.text;
    }

    if (
      tempProfile.phone &&
      sanitizeDigitsOnly(tempProfile.phone) !== tempProfile.phone
    ) {
      errors.phone = professionalMessages.number;
    }

    if (
      tempProfile.nif &&
      sanitizeDigitsOnly(tempProfile.nif) !== tempProfile.nif
    ) {
      errors.nif = professionalMessages.number;
    }

    if (
      tempProfile.nis &&
      sanitizeDigitsOnly(tempProfile.nis) !== tempProfile.nis
    ) {
      errors.nis = professionalMessages.number;
    }

    if (
      tempProfile.rc_number &&
      sanitizeDigitsOnly(tempProfile.rc_number) !== tempProfile.rc_number
    ) {
      errors.rc_number = professionalMessages.number;
    }

    if (
      tempProfile.workforce_total &&
      sanitizeDigitsOnly(tempProfile.workforce_total) !==
        tempProfile.workforce_total
    ) {
      errors.workforce_total = professionalMessages.number;
    }

    if (tempProfile.email && !validateEmail(tempProfile.email)) {
      errors.email = professionalMessages.email;
    }

    if (tempProfile.website && !validateWebsite(tempProfile.website)) {
      errors.website = professionalMessages.website;
    }

    if (!tempProfile.wilaya) {
      errors.wilaya = professionalMessages.requiredWilaya;
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const loadProfile = async () => {
    try {
      const res = await axios.get(
        `${backendUrl}/api/suppliers/profile/${id_user}`,
      );
      setProfile(normalizeProfileData(res.data));
    } catch (err) {
      console.error(err);
    }
  };

  const loadProducts = async () => {
    try {
      const res = await axios.get(
        `${backendUrl}/api/supplier-products/${id_user}`,
      );
      setProducts(res.data);
      setStats((prev) => ({ ...prev, products: res.data.length }));
    } catch (err) {
      console.error(err);
    }
  };

  const loadMachines = async () => {
    try {
      const res = await axios.get(
        `${backendUrl}/api/supplier-machines/${id_user}`,
      );
      setMachines(res.data);
      setStats((prev) => ({ ...prev, machines: res.data.length }));
    } catch (err) {
      console.error(err);
    }
  };

  const loadCertifications = async () => {
    try {
      const res = await axios.get(
        `${backendUrl}/api/supplier-certifications/${id_user}`,
      );
      setCertifications(res.data);
      setStats((prev) => ({ ...prev, certifications: res.data.length }));
    } catch (err) {
      console.error(err);
    }
  };

  const loadDocuments = async () => {
    try {
      const res = await axios.get(
        `${backendUrl}/api/supplier-documents/${id_user}`,
      );
      setDocuments(res.data || []);
    } catch (err) {
      console.error(err);
      setDocuments([]);
    }
  };

  const loadActivity = async () => {
    try {
      const res = await axios.get(
        `${backendUrl}/api/supplier-activity/${id_user}`,
      );
      setActivity(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const loadCapabilities = async () => {
    try {
      const res = await axios.get(
        `${backendUrl}/api/suppliers/capabilities/${id_user}`,
      );
      setCapabilities(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const loadAllSegments = async () => {
    try {
      const res = await axios.get(`${backendUrl}/api/segments`);
      setAllSegments(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const loadAll = async () => {
    await Promise.all([
      loadProfile(),
      loadProducts(),
      loadMachines(),
      loadCertifications(),
      loadDocuments(),
      loadActivity(),
      loadCapabilities(),
      loadAllSegments(),
    ]);
    setLoading(false);
  };

  useEffect(() => {
    setTempProfile(profile);
  }, [profile]);

  useEffect(() => {
    setLoading(true);
    setIsEditing(false);
    loadAll();
  }, [id_user]);

  useEffect(() => {
    setSelectedCapabilities(capabilities.map((c) => c.id_segment));
  }, [capabilities]);

  const handleSaveCapabilities = async () => {
    try {
      await axios.post(`${backendUrl}/api/suppliers/capabilities/save`, {
        id_user,
        segmentIds: selectedCapabilities,
      });
      await loadCapabilities();
      setIsEditingCapabilities(false);
    } catch (err) {
      console.error(err);
      alert("Error saving capabilities");
    }
  };

  const toggleCapability = (id_segment: number) => {
    setSelectedCapabilities((prev) =>
      prev.includes(id_segment)
        ? prev.filter((id) => id !== id_segment)
        : [...prev, id_segment],
    );
  };

  const getCapabilityIcon = (name: string) => {
    const n = name.toLowerCase();
    if (n.includes("metal")) return <FaIndustry color="#64748b" />;
    if (n.includes("plastic")) return <FaCubes color="#3b82f6" />;
    if (n.includes("electronic")) return <FaMicrochip color="#8b5cf6" />;
    if (n.includes("mechanical")) return <FaCogs color="#f59e0b" />;
    if (n.includes("textile")) return <FaTools color="#ec4899" />;
    return <FaIndustry color="#16a34a" />;
  };

  const handleSave = async () => {
    if (!validateFields()) return;

    try {
      const formData = new FormData();
      Object.keys(tempProfile).forEach((key) => {
        formData.append(key, (tempProfile as any)[key]);
      });
      formData.append("id_user", id_user);

      if (selectedFile) {
        formData.append("logo", selectedFile);
      }

      await axios.post(`${backendUrl}/api/suppliers/update-profile`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      await loadProfile();
      window.dispatchEvent(new Event("profile-updated"));
      setIsEditing(false);
      setSelectedFile(null);
      setLogoPreview(null);
    } catch (err) {
      console.error(err);
      alert("Error updating profile");
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setSelectedFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const completion =
    ((profile.legal_name ? 1 : 0) +
      (products.length > 0 ? 1 : 0) +
      (machines.length > 0 ? 1 : 0) +
      (certifications.length > 0 ? 1 : 0)) *
    25;

  const isCompanyCompleted =
    !!profile.legal_name &&
    !!profile.email &&
    !!profile.phone &&
    !!profile.country &&
    !!profile.wilaya &&
    !!profile.trade_name &&
    !!profile.rc_number;

  const hasProducts = products.length > 0;
  const hasMachines = machines.length > 0;
  const hasCertifications = certifications.length > 0;

  const currentLogoSrc = logoPreview
    ? logoPreview
    : profile.logo
      ? `${backendUrl}${profile.logo}`
      : DEFAULT_USER_IMAGE;

  const isDefaultAvatar = !logoPreview && !profile.logo;

  const progressVariantColor = useMemo(() => {
    if (completion >= 100) return "#22c55e";
    if (completion >= 50) return "#facc15";
    return "#f59e0b";
  }, [completion]);

  const renderOverview = () => (
    <Card className="border-0" style={shellCardStyle}>
      <Card.Body className="p-4 p-lg-5">
        <div className="mb-4">
          <h4 style={sectionTitleStyle}>Profile Overview</h4>
        </div>

        <Row className="g-4">
          <Col md={6}>
            <InfoCard
              label="Company Name"
              value={displayValue(profile.legal_name)}
              icon={<FaBuilding />}
            />
          </Col>
          <Col md={6}>
            <InfoCard
              label="Trade Name"
              value={displayValue(profile.trade_name)}
              icon={<FaBuilding />}
            />
          </Col>
          <Col md={6}>
            <InfoCard
              label="NIF"
              value={displayValue(profile.nif)}
              icon={<FaIdCard />}
              iconBg="#fef3c7"
              iconColor="#d4a106"
            />
          </Col>
          <Col md={6}>
            <InfoCard
              label="NIS"
              value={displayValue(profile.nis)}
              icon={<FaIdCard />}
              iconBg="#fef3c7"
              iconColor="#d4a106"
            />
          </Col>
          <Col md={6}>
            <InfoCard
              label="RC Number"
              value={displayValue(profile.rc_number)}
              icon={<FaIdCard />}
              iconBg="#fef3c7"
              iconColor="#d4a106"
            />
          </Col>
          <Col md={6}>
            <InfoCard
              label="Workforce"
              value={displayValue(profile.workforce_total)}
              icon={<FaIndustry />}
              iconBg="#fef3c7"
              iconColor="#d4a106"
            />
          </Col>
          <Col md={6}>
            <InfoCard
              label="Country"
              value={displayValue(profile.country)}
              icon={<FaMapMarkedAlt />}
              iconBg="#f3e8ff"
              iconColor="#7c3aed"
            />
          </Col>
          <Col md={6}>
            <InfoCard
              label="City"
              value={displayValue(profile.wilaya)}
              icon={<FaCity />}
              iconBg="#f3e8ff"
              iconColor="#7c3aed"
            />
          </Col>
          <Col md={6}>
            <InfoCard
              label="Address"
              value={displayValue(profile.address)}
              icon={<FaMapMarkerAlt />}
              iconBg="#eef2f7"
              iconColor="#4b5563"
            />
          </Col>

          <Col md={6}>
            <Card className="border-2" style={infoCardStyle}>
              <div style={{ ...smallLabelStyle, marginBottom: 8 }}>Status</div>
              <div>{getStatusBadge(profile.industrial_status)}</div>
            </Card>
          </Col>
        </Row>

        <Card
          className="border-2 mt-4"
          style={{ ...shellCardStyle, boxShadow: "none" }}
        >
          <Card.Body className="p-3 pt-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4 style={sectionTitleStyle}>Main Capabilities</h4>

              {canManageSupplierProfile &&
                (!isEditingCapabilities ? (
                  <Button
                    size="sm"
                    style={{
                      backgroundColor: "#facc15",
                      border: "none",
                      color: "#111827",
                      fontWeight: 600,
                      padding: "8px 14px",
                      borderRadius: 10,
                      fontFamily: FONT_STACK,
                    }}
                    onClick={() => setIsEditingCapabilities(true)}
                  >
                    Edit Capabilities
                  </Button>
                ) : (
                  <div className="d-flex gap-2">
                    <Button
                      size="sm"
                      style={{
                        backgroundColor: "#f1f5f9",
                        border: "1px solid #e2e8f0",
                        color: "#334155",
                        fontWeight: 600,
                        padding: "8px 14px",
                        borderRadius: 10,
                        fontFamily: FONT_STACK,
                      }}
                      onClick={() => {
                        setSelectedCapabilities(
                          capabilities.map((c) => c.id_segment),
                        );
                        setIsEditingCapabilities(false);
                      }}
                    >
                      Cancel
                    </Button>

                    <Button
                      size="sm"
                      style={{
                        backgroundColor: "#16a34a",
                        border: "none",
                        color: "#fff",
                        fontWeight: 600,
                        padding: "8px 14px",
                        borderRadius: 10,
                        fontFamily: FONT_STACK,
                      }}
                      onClick={handleSaveCapabilities}
                    >
                      Save
                    </Button>
                  </div>
                ))}
            </div>

            {isEditingCapabilities ? (
              <Row className="g-3">
                {allSegments.map((seg) => (
                  <Col md={4} key={seg.id_segment}>
                    <div
                      style={{
                        background: "#ffffff",
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: 16,
                        padding: "13px 14px",
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                      }}
                    >
                      <Form.Check
                        type="checkbox"
                        checked={selectedCapabilities.includes(seg.id_segment)}
                        onChange={() => toggleCapability(seg.id_segment)}
                      />
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          fontFamily: FONT_STACK,
                        }}
                      >
                        {getCapabilityIcon(seg.name)}
                        <span
                          style={{
                            fontWeight: 700,
                            color: COLORS.textPrimary,
                          }}
                        >
                          {seg.name}
                        </span>
                      </span>
                    </div>
                  </Col>
                ))}
              </Row>
            ) : capabilities.length === 0 ? (
              <div
                style={{
                  color: COLORS.textSecondary,
                  fontSize: 14,
                  background: "#ffffff",
                  border: `1px dashed ${COLORS.borderStrong}`,
                  borderRadius: 16,
                  padding: "18px",
                  fontFamily: FONT_STACK,
                }}
              >
                No capabilities defined yet
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 12,
                }}
              >
                {capabilities.map((cap) => (
                  <div
                    key={cap.id_segment}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      padding: "11px 15px",
                      borderRadius: 999,
                      background: "#ffffff",
                      border: `1px solid ${COLORS.border}`,
                      color: COLORS.textPrimary,
                      fontSize: 14,
                      fontWeight: 700,
                      lineHeight: 1,
                      fontFamily: FONT_STACK,
                    }}
                  >
                    <span
                      style={{
                        marginRight: 10,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {getCapabilityIcon(cap.name)}
                    </span>
                    {cap.name}
                  </div>
                ))}
              </div>
            )}
          </Card.Body>
        </Card>

        <Card
          className="border-2 mt-4"
          style={{ ...shellCardStyle, boxShadow: "none" }}
        >
          <Card.Body className="p-3 pt-4">
            <div className="mb-3">
              <h4 style={sectionTitleStyle}>Key Certifications</h4>
            </div>

            {certifications.length === 0 ? (
              <div
                style={{
                  color: COLORS.textSecondary,
                  fontSize: 14,
                  fontFamily: FONT_STACK,
                }}
              >
                No certifications available yet.
              </div>
            ) : (
              certifications.map((c) => (
                <div
                  key={c.id}
                  className="d-flex justify-content-between align-items-center mb-2"
                  style={{
                    padding: "10px 0",
                    borderBottom: `1px solid ${COLORS.border}`,
                    fontFamily: FONT_STACK,
                  }}
                >
                  <span
                    style={{
                      color: COLORS.textPrimary,
                      fontWeight: 600,
                    }}
                  >
                    {c.certification_name}
                  </span>
                  <span className="badge bg-success-subtle text-success">
                    Active
                  </span>
                </div>
              ))
            )}
          </Card.Body>
        </Card>
      </Card.Body>
    </Card>
  );

  const renderCompany = () => (
    <Card className="border-0" style={shellCardStyle}>
      <Card.Body className="p-4 p-lg-5">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4 style={sectionTitleStyle}>Company Information</h4>

          {canManageSupplierProfile &&
            (!isEditing ? (
              <Button
                size="sm"
                style={{
                  backgroundColor: "#facc15",
                  border: "none",
                  color: "#111827",
                  fontWeight: 600,
                  padding: "8px 14px",
                  borderRadius: 10,
                  fontFamily: FONT_STACK,
                }}
                onClick={() => {
                  setTempProfile({
                    ...profile,
                    workforce_total: sanitizeDigitsOnly(
                      profile.workforce_total,
                    ),
                    nif: sanitizeDigitsOnly(profile.nif),
                    nis: sanitizeDigitsOnly(profile.nis),
                    rc_number: sanitizeDigitsOnly(profile.rc_number),
                    phone: sanitizeDigitsOnly(profile.phone || ""),
                  });
                  setFieldErrors({});
                  setIsEditing(true);
                }}
              >
                Edit Profile
              </Button>
            ) : (
              <div className="d-flex gap-2">
                <Button
                  size="sm"
                  style={{
                    backgroundColor: "#f1f5f9",
                    border: "1px solid #e2e8f0",
                    color: "#334155",
                    fontWeight: 600,
                    padding: "8px 14px",
                    borderRadius: 10,
                    fontFamily: FONT_STACK,
                  }}
                  onClick={() => {
                    setTempProfile(profile);
                    setFieldErrors({});
                    setIsEditing(false);
                  }}
                >
                  Cancel
                </Button>

                <Button
                  size="sm"
                  style={{
                    backgroundColor: "#16a34a",
                    border: "none",
                    color: "#fff",
                    fontWeight: 600,
                    padding: "8px 14px",
                    borderRadius: 10,
                    fontFamily: FONT_STACK,
                  }}
                  onClick={handleSave}
                >
                  Save
                </Button>
              </div>
            ))}
        </div>

        <Row className="g-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label className="text-muted small">Company Name</Form.Label>
              <Form.Control
                value={
                  isEditing
                    ? tempProfile.legal_name
                    : displayValue(profile.legal_name)
                }
                onChange={(e) =>
                  handleTextFieldChange("legal_name", e.target.value)
                }
                readOnly={!isEditing}
                isInvalid={!!fieldErrors.legal_name}
                className={`rounded-3 py-2 ${isEditing ? "" : "bg-white border"}`}
              />
              <Form.Control.Feedback type="invalid">
                {fieldErrors.legal_name}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group>
              <Form.Label className="text-muted small">Trade Name</Form.Label>
              <Form.Control
                value={
                  isEditing
                    ? tempProfile.trade_name
                    : displayValue(profile.trade_name)
                }
                onChange={(e) =>
                  handleTextFieldChange("trade_name", e.target.value)
                }
                readOnly={!isEditing}
                isInvalid={!!fieldErrors.trade_name}
                className={`rounded-3 py-2 ${isEditing ? "" : "bg-white border"}`}
              />
              <Form.Control.Feedback type="invalid">
                {fieldErrors.trade_name}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group>
              <Form.Label className="text-muted small">NIF</Form.Label>
              <Form.Control
                value={isEditing ? tempProfile.nif : displayValue(profile.nif)}
                onChange={(e) => handleNumberFieldChange("nif", e.target.value)}
                readOnly={!isEditing}
                inputMode="numeric"
                isInvalid={!!fieldErrors.nif}
                className={`rounded-3 py-2 ${isEditing ? "" : "bg-white border"}`}
              />
              <Form.Control.Feedback type="invalid">
                {fieldErrors.nif}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group>
              <Form.Label className="text-muted small">NIS</Form.Label>
              <Form.Control
                value={isEditing ? tempProfile.nis : displayValue(profile.nis)}
                onChange={(e) => handleNumberFieldChange("nis", e.target.value)}
                readOnly={!isEditing}
                inputMode="numeric"
                isInvalid={!!fieldErrors.nis}
                className={`rounded-3 py-2 ${isEditing ? "" : "bg-white border"}`}
              />
              <Form.Control.Feedback type="invalid">
                {fieldErrors.nis}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group>
              <Form.Label className="text-muted small">RC Number</Form.Label>
              <Form.Control
                value={
                  isEditing
                    ? tempProfile.rc_number
                    : displayValue(profile.rc_number)
                }
                onChange={(e) =>
                  handleNumberFieldChange("rc_number", e.target.value)
                }
                readOnly={!isEditing}
                inputMode="numeric"
                isInvalid={!!fieldErrors.rc_number}
                className={`rounded-3 py-2 ${isEditing ? "" : "bg-white border"}`}
              />
              <Form.Control.Feedback type="invalid">
                {fieldErrors.rc_number}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group>
              <Form.Label className="text-muted small">Workforce</Form.Label>
              <Form.Control
                type="text"
                value={
                  isEditing
                    ? tempProfile.workforce_total || ""
                    : displayValue(profile.workforce_total)
                }
                onChange={(e) =>
                  handleNumberFieldChange("workforce_total", e.target.value)
                }
                readOnly={!isEditing}
                inputMode="numeric"
                isInvalid={isEditing && !!fieldErrors.workforce_total}
                className={`rounded-3 py-2 ${isEditing ? "" : "bg-white border"}`}
              />
              <Form.Control.Feedback type="invalid">
                {fieldErrors.workforce_total}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group>
              <Form.Label className="text-muted small">Website</Form.Label>
              <Form.Control
                value={
                  isEditing
                    ? tempProfile.website
                    : displayValue(profile.website)
                }
                onChange={(e) => handleWebsiteChange(e.target.value)}
                readOnly={!isEditing}
                isInvalid={!!fieldErrors.website}
                className={`rounded-3 py-2 ${isEditing ? "" : "bg-white border"}`}
              />
              <Form.Control.Feedback type="invalid">
                {fieldErrors.website}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group>
              <Form.Label className="text-muted small">
                Contact Email
              </Form.Label>
              <Form.Control
                value={
                  isEditing ? tempProfile.email : displayValue(profile.email)
                }
                onChange={(e) => handleEmailChange(e.target.value)}
                readOnly={!isEditing}
                isInvalid={!!fieldErrors.email}
                className={`rounded-3 py-2 ${isEditing ? "" : "bg-white border"}`}
              />
              <Form.Control.Feedback type="invalid">
                {fieldErrors.email}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group>
              <Form.Label className="text-muted small">Country</Form.Label>
              <Form.Control
                value={
                  isEditing
                    ? tempProfile.country
                    : displayValue(profile.country)
                }
                onChange={(e) =>
                  handleTextFieldChange("country", e.target.value)
                }
                readOnly={!isEditing}
                placeholder="Enter country"
                isInvalid={!!fieldErrors.country}
                className={`rounded-3 py-2 ${isEditing ? "" : "bg-white border"}`}
              />
              <Form.Control.Feedback type="invalid">
                {fieldErrors.country}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group>
              <Form.Label className="text-muted small">City</Form.Label>

              {isEditing ? (
                <>
                  <Select
                    options={wilayaOptions}
                    value={
                      wilayaOptions.find(
                        (option) => option.value === tempProfile.wilaya,
                      ) || null
                    }
                    onChange={handleWilayaChange}
                    placeholder="Select wilaya..."
                    isSearchable
                  />
                  {fieldErrors.wilaya && (
                    <div
                      style={{
                        color: "#dc3545",
                        fontSize: "0.875rem",
                        marginTop: "6px",
                        fontFamily: FONT_STACK,
                      }}
                    >
                      {fieldErrors.wilaya}
                    </div>
                  )}
                </>
              ) : (
                <Form.Control
                  value={displayValue(profile.wilaya)}
                  readOnly
                  className="rounded-3 py-2 bg-white border"
                />
              )}
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group>
              <Form.Label className="text-muted small">Phone</Form.Label>
              <Form.Control
                value={
                  isEditing ? tempProfile.phone : displayValue(profile.phone)
                }
                onChange={(e) =>
                  handleNumberFieldChange("phone", e.target.value)
                }
                readOnly={!isEditing}
                inputMode="numeric"
                isInvalid={!!fieldErrors.phone}
                className={`rounded-3 py-2 ${isEditing ? "" : "bg-white border"}`}
              />
              <Form.Control.Feedback type="invalid">
                {fieldErrors.phone}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group>
              <Form.Label className="text-muted small">Status</Form.Label>
              {isEditing ? (
                <Form.Select
                  value={tempProfile.industrial_status || "incomplete"}
                  onChange={(e) =>
                    setTempProfile({
                      ...tempProfile,
                      industrial_status: e.target.value,
                    })
                  }
                  className="rounded-3 py-2"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="incomplete">Incomplete</option>
                </Form.Select>
              ) : (
                <div style={{ marginTop: 6 }}>
                  {getStatusBadge(profile.industrial_status)}
                </div>
              )}
            </Form.Group>
          </Col>

          <Col md={12}>
            <Form.Group>
              <Form.Label className="text-muted small">Address</Form.Label>
              <Form.Control
                value={
                  isEditing
                    ? tempProfile.address
                    : displayValue(profile.address)
                }
                onChange={(e) =>
                  setTempProfile({
                    ...tempProfile,
                    address: e.target.value,
                  })
                }
                readOnly={!isEditing}
                className={`rounded-3 py-2 ${isEditing ? "" : "bg-white border"}`}
              />
            </Form.Group>
          </Col>

          <Col md={12}>
            <Form.Group>
              <Form.Label className="text-muted small">Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={
                  isEditing
                    ? tempProfile.description
                    : displayValue(profile.description)
                }
                onChange={(e) =>
                  setTempProfile({
                    ...tempProfile,
                    description: e.target.value,
                  })
                }
                readOnly={!isEditing}
                placeholder="Write a short company description..."
                className={`rounded-3 ${isEditing ? "" : "bg-white border"}`}
              />
            </Form.Group>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );

  const renderProducts = () => (
    <Card className="border-0" style={shellCardStyle}>
      <Card.Body className="p-4 p-lg-5">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 style={sectionTitleStyle}>Products</h4>
          {canManageSupplierProfile && (
            <Link to={`/SupplierProducts?id_user=${id_user}`}>
              <Button
                size="sm"
                style={{
                  backgroundColor: "#facc15",
                  border: "none",
                  color: "#111827",
                  fontWeight: 600,
                  padding: "8px 14px",
                  borderRadius: 10,
                  fontFamily: FONT_STACK,
                }}
              >
                Manage Product
              </Button>
            </Link>
          )}
        </div>

        <div style={tableWrapStyle}>
          <Table hover className="mb-0">
            <thead>
              <tr>
                <th>Product Name</th>
                <th>Category</th>
                <th>Material</th>
                <th>Capacity</th>
                <th>Unit</th>
              </tr>
            </thead>
            <tbody>
              {products.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center text-muted py-4">
                    No products added yet
                  </td>
                </tr>
              ) : (
                products.map((p) => (
                  <tr key={p.id_product}>
                    <td>{p.product_name}</td>
                    <td>{p.category}</td>
                    <td>{p.material}</td>
                    <td>{p.annual_capacity}</td>
                    <td>{p.unit}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      </Card.Body>
    </Card>
  );

  const renderMachines = () => (
    <Card className="border-0" style={shellCardStyle}>
      <Card.Body className="p-4 p-lg-5">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 style={sectionTitleStyle}>Machines</h4>

          {canManageSupplierProfile && (
            <Link to={`/SupplierMachines?id_user=${id_user}`}>
              <Button
                size="sm"
                style={{
                  backgroundColor: "#facc15",
                  border: "none",
                  color: "#111827",
                  fontWeight: 600,
                  padding: "8px 14px",
                  borderRadius: 10,
                  fontFamily: FONT_STACK,
                }}
              >
                Manage Machine
              </Button>
            </Link>
          )}
        </div>

        <div style={tableWrapStyle}>
          <Table hover responsive className="mb-0">
            <thead>
              <tr>
                <th>Machine</th>
                <th>Type</th>
                <th>Manufacturer</th>
                <th>Capacity</th>
                <th>Quantity</th>
              </tr>
            </thead>
            <tbody>
              {machines.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center text-muted py-4">
                    No machines added yet
                  </td>
                </tr>
              ) : (
                machines.map((m) => (
                  <tr key={m.id_machine}>
                    <td>{m.machine_name}</td>
                    <td>{m.machine_type}</td>
                    <td>{m.manufacturer}</td>
                    <td>{m.capacity}</td>
                    <td>{m.quantity}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      </Card.Body>
    </Card>
  );

  const renderCertifications = () => (
    <Card className="border-0" style={shellCardStyle}>
      <Card.Body className="p-4 p-lg-5">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 style={sectionTitleStyle}>Certifications</h4>
          {canManageSupplierProfile && (
            <Link to={`/SupplierCertifications?id_user=${id_user}`}>
              <Button
                size="sm"
                style={{
                  backgroundColor: "#facc15",
                  border: "none",
                  color: "#111827",
                  fontWeight: 600,
                  padding: "8px 14px",
                  borderRadius: 10,
                  fontFamily: FONT_STACK,
                }}
              >
                Manage Certification
              </Button>
            </Link>
          )}
        </div>

        <div style={tableWrapStyle}>
          <Table hover className="mb-0">
            <thead>
              <tr>
                <th>Name</th>
                <th>Number</th>
                <th>Issued</th>
                <th>Expiry</th>
              </tr>
            </thead>
            <tbody>
              {certifications.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center text-muted py-4">
                    No certifications added yet
                  </td>
                </tr>
              ) : (
                certifications.map((c) => (
                  <tr key={c.id}>
                    <td>{c.certification_name}</td>
                    <td>{c.certificate_number || "-"}</td>
                    <td>{formatDate(c.issued_date)}</td>
                    <td>{formatDate(c.expiry_date)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      </Card.Body>
    </Card>
  );

  const renderDocuments = () => (
    <Card className="border-0" style={shellCardStyle}>
      <Card.Body className="p-4 p-lg-5">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 style={sectionTitleStyle}>Documents</h4>

          {canManageSupplierProfile && (
            <Link to={`/SupplierDocuments?id_user=${id_user}`}>
              <Button
                size="sm"
                style={{
                  backgroundColor: "#facc15",
                  border: "none",
                  color: "#111827",
                  fontWeight: 600,
                  padding: "8px 14px",
                  borderRadius: 10,
                  fontFamily: FONT_STACK,
                }}
              >
                Manage Documents
              </Button>
            </Link>
          )}
        </div>

        {documents.length === 0 ? (
          <div
            className="text-center"
            style={{
              padding: "40px 20px",
              borderRadius: 16,
              background: "#ffffff",
              border: `1px dashed ${COLORS.borderStrong}`,
              fontFamily: FONT_STACK,
            }}
          >
            <div
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: COLORS.textPrimary,
              }}
            >
              No documents uploaded yet
            </div>

            <div
              style={{
                marginTop: 8,
                marginBottom: 18,
                color: COLORS.textSecondary,
                fontSize: 14,
              }}
            >
              Upload company files, brochures, technical sheets, or
              certificates.
            </div>

            {canManageSupplierProfile && (
              <Link to={`/SupplierDocuments?id_user=${id_user}`}>
                <Button
                  size="sm"
                  style={{
                    backgroundColor: "#facc15",
                    border: "none",
                    color: "#111827",
                    fontWeight: 600,
                    padding: "8px 18px",
                    borderRadius: 10,
                    fontFamily: FONT_STACK,
                  }}
                >
                  Upload Documents
                </Button>
              </Link>
            )}
          </div>
        ) : (
          <div style={tableWrapStyle}>
            <Table hover className="mb-0">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>File</th>
                </tr>
              </thead>
              <tbody>
                {documents.map((doc) => (
                  <tr key={doc.id_document}>
                    <td>{doc.document_type}</td>
                    <td>
                      {!doc.file_path ? (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "6px 16px",
                            borderRadius: "999px",
                            fontSize: "13px",
                            fontWeight: 600,
                            background: "#f3f4f6",
                            color: "#9ca3af",
                            border: "1px solid #e5e7eb",
                            fontFamily: FONT_STACK,
                          }}
                        >
                          No file
                        </span>
                      ) : (
                        <a
                          href={`${backendUrl}/uploads/documents/${doc.file_path}`}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "6px",
                            padding: "6px 16px",
                            borderRadius: "999px",
                            fontSize: "13px",
                            fontWeight: 600,
                            textDecoration: "none",
                            background: "#fff7ed",
                            color: "#d97706",
                            border: "1px solid #fbbf24",
                            fontFamily: FONT_STACK,
                          }}
                        >
                          <FaEye size={12} /> View
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Card.Body>
    </Card>
  );

  const renderActivity = () => (
    <Row className="g-4">
      <Col md={5}>
        <Card className="border-0" style={shellCardStyle}>
          <Card.Body className="p-4 p-lg-5">
            <div style={{ marginBottom: 24 }}>
              <h4 style={sectionTitleStyle}>Supplier Activity Timeline</h4>
            </div>

            {activity.length === 0 && (
              <div
                className="text-center"
                style={{
                  color: COLORS.textSecondary,
                  padding: "24px 0",
                  fontSize: 14,
                  fontFamily: FONT_STACK,
                }}
              >
                No activity yet
              </div>
            )}

            {activity.map((item: any) => {
              let label = "New RFQ received";
              let date = item.created_at;
              let dotColor = "#ef4444";
              let subtitle = item.title;

              if (item.awarded_at) {
                label = "RFQ Awarded";
                date = item.awarded_at;
                dotColor = "#16a34a";
              } else if (item.shortlisted_at) {
                label = "Shortlisted";
                date = item.shortlisted_at;
                dotColor = "#eab308";
              } else if (item.response_created_at) {
                label = "Responded to RFQ";
                date = item.response_created_at;
                dotColor = "#0ea5e9";
              } else if (item.viewed_at) {
                label = "Viewed RFQ";
                date = item.viewed_at;
                dotColor = "#8b5cf6";
              }

              return (
                <div
                  key={`${item.id_rfq}-${label}`}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 14,
                    paddingBottom: 18,
                    marginBottom: 18,
                    borderBottom: `1px solid ${COLORS.border}`,
                    fontFamily: FONT_STACK,
                  }}
                >
                  <div
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      backgroundColor: dotColor,
                      marginTop: 8,
                      flexShrink: 0,
                    }}
                  />

                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: 17,
                        fontWeight: 700,
                        color: COLORS.textPrimary,
                        lineHeight: 1.3,
                        marginBottom: 4,
                      }}
                    >
                      {label}
                    </div>

                    <div
                      style={{
                        fontSize: 15,
                        color: "#475569",
                        marginBottom: 6,
                      }}
                    >
                      {subtitle}
                    </div>

                    <div
                      style={{
                        fontSize: 13,
                        color: COLORS.textMuted,
                      }}
                    >
                      {new Date(date).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              );
            })}
          </Card.Body>
        </Card>
      </Col>

      <Col md={7}>
        <Card className="border-0" style={shellCardStyle}>
          <Card.Body className="p-4 p-lg-5">
            <div style={{ marginBottom: 24 }}>
              <h4 style={sectionTitleStyle}>RFQ History</h4>
            </div>

            <div style={tableWrapStyle}>
              <Table hover className="mb-0">
                <thead>
                  <tr>
                    <th>RFQ</th>
                    <th>Segment</th>
                    <th>Status</th>
                    <th>Deadline</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {activity.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center text-muted py-4">
                        No RFQs received yet
                      </td>
                    </tr>
                  ) : (
                    activity.map((item) => {
                      let statusLabel = "Received";

                      if (item.awarded_at) statusLabel = "Awarded";
                      else if (item.shortlisted_at) statusLabel = "Shortlisted";
                      else if (item.id_response) statusLabel = "Responded";
                      else if (item.viewed_at) statusLabel = "Viewed";
                      else if (item.id_match) statusLabel = "Matched";

                      return (
                        <tr key={item.id_rfq}>
                          <td>{item.title}</td>
                          <td>{item.segment_name || "-"}</td>
                          <td>
                            <span
                              style={{
                                padding: "5px 10px",
                                borderRadius: 999,
                                fontSize: 12,
                                fontWeight: 700,
                                background:
                                  statusLabel === "Awarded"
                                    ? "#dcfce7"
                                    : statusLabel === "Shortlisted"
                                      ? "#fef3c7"
                                      : statusLabel === "Responded"
                                        ? "#e0f2fe"
                                        : statusLabel === "Viewed"
                                          ? "#ede9fe"
                                          : "#f1f5f9",
                                color:
                                  statusLabel === "Awarded"
                                    ? "#15803d"
                                    : statusLabel === "Shortlisted"
                                      ? "#92400e"
                                      : statusLabel === "Responded"
                                        ? "#0369a1"
                                        : statusLabel === "Viewed"
                                          ? "#7c3aed"
                                          : "#334155",
                                fontFamily: FONT_STACK,
                              }}
                            >
                              {statusLabel}
                            </span>
                          </td>
                          <td>
                            {item.deadline
                              ? new Date(item.deadline).toLocaleDateString()
                              : "-"}
                          </td>
                          <td>
                            <Link
                              to={`/supplier-rfq/${item.id_rfq}?id_user=${id_user}`}
                            >
                              <Button
                                size="sm"
                                style={{
                                  backgroundColor: "#facc15",
                                  border: "none",
                                  color: "#111827",
                                  fontWeight: 600,
                                  padding: "6px 12px",
                                  borderRadius: 10,
                                  fontFamily: FONT_STACK,
                                }}
                              >
                                View RFQ
                              </Button>
                            </Link>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case "overview":
        return renderOverview();
      case "company":
        return renderCompany();
      case "products":
        return renderProducts();
      case "machines":
        return renderMachines();
      case "certifications":
        return renderCertifications();
      case "documents":
        return renderDocuments();
      case "activity":
        return renderActivity();
      default:
        return renderOverview();
    }
  };

  if (loading) {
    return (
      <div
        className="container-fluid d-flex align-items-center justify-content-center"
        style={{
          minHeight: "100vh",
          background: "#ffffff",
          fontFamily: FONT_STACK,
        }}
      >
        <div className="text-center">
          <Spinner animation="border" style={{ color: COLORS.brand }} />
          <div
            style={{
              marginTop: 12,
              color: COLORS.textSecondary,
              fontWeight: 600,
            }}
          >
            Loading supplier profile...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="container-fluid"
      style={{
        padding: "28px 36px",
        background: "#ffffff",
        minHeight: "100vh",
        fontFamily: FONT_STACK,
      }}
    >
      <Row className="g-4" style={{ alignItems: "flex-start" }}>
        <Col md={3}>
          <Card
            className="border-0 mb-4"
            style={{
              ...shellCardStyle,
              overflow: "hidden",
              position: "relative",
            }}
          >
            <div
              style={{
                height: 50,
                background: "linear-gradient(90deg, #f4c400 0%, #ffd54a 100%)",
              }}
            />

            <Card.Body
              style={{
                padding: "82px 22px 24px",
                position: "relative",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: -34,
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: 116,
                  height: 116,
                  borderRadius: "50%",
                  overflow: "hidden",
                  border: isEditing ? "2px solid #e6deb3" : "2px solid #ffffff",
                  boxShadow: isEditing
                    ? "0 0 0 6px rgba(250, 204, 21, 0.18), 0 10px 24px rgba(15, 23, 42, 0.12)"
                    : "0 10px 24px rgba(15, 23, 42, 0.12)",
                  background: "#ffffff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: isEditing ? "pointer" : "default",
                }}
                onClick={() => {
                  if (isEditing) {
                    document.getElementById("logoInput")?.click();
                  }
                }}
              >
                <img
                  src={currentLogoSrc}
                  alt="logo"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).src =
                      DEFAULT_USER_IMAGE;
                  }}
                  style={{
                    width: "100%",
                    height: "100%",
                    display: "block",
                    borderRadius: "50%",
                    background: "#ffffff",
                    objectPosition: "center center",
                    objectFit: isDefaultAvatar ? "contain" : "cover",
                    padding: isDefaultAvatar ? "14px" : "0px",
                    transition: "0.25s ease",
                  }}
                />

                {isEditing && (
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      background: "rgba(15, 23, 42, 0.35)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#ffffff",
                      fontSize: 12,
                      fontWeight: 700,
                      textAlign: "center",
                      padding: "8px",
                    }}
                  >
                    Change Photo
                  </div>
                )}
              </div>

              {isEditing && (
                <input
                  id="logoInput"
                  type="file"
                  style={{ display: "none" }}
                  onChange={handleLogoChange}
                />
              )}

              <h3
                style={{
                  fontSize: 25,
                  fontWeight: 600,
                  color: COLORS.textPrimary,
                  marginTop: 18,
                  marginBottom: 6,
                  letterSpacing: "-0.03em",
                  fontFamily: FONT_STACK,
                }}
              >
                {profile.legal_name || "Supplier Name"}
              </h3>

              <div
                style={{
                  fontSize: 15,
                  color: COLORS.textSecondary,
                  fontWeight: 600,
                  marginBottom: 12,
                  fontFamily: FONT_STACK,
                }}
              >
                {profile.wilaya || "-"}
                {profile.country ? `, ${profile.country}` : ""}
              </div>

              <p
                style={{
                  fontSize: 13,
                  lineHeight: 1.8,
                  color: COLORS.textSecondary,
                  maxWidth: 280,
                  minHeight: 46,
                  margin: "0 auto 18px",
                  fontFamily: FONT_STACK,
                }}
              >
                {profile.description || ""}
              </p>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  padding: "11px 18px",
                  borderRadius: 999,
                  background: COLORS.successBg,
                  color: COLORS.successText,
                  border: `1px solid ${COLORS.successBorder}`,
                  fontSize: 14,
                  fontWeight: 700,
                  boxShadow: "0 2px 8px rgba(34, 197, 94, 0.08)",
                }}
              >
                <span>✓</span>
                <span>Verified Supplier</span>
              </div>
              <div
                style={{
                  height: 1,
                  background: COLORS.border,
                  margin: "24px 0 20px",
                }}
              />

              <Row className="g-3">
                <Col xs={4}>
                  <MetricCard label="Products" value={stats.products} />
                </Col>
                <Col xs={4}>
                  <MetricCard label="Machines" value={stats.machines} />
                </Col>
                <Col xs={4}>
                  <MetricCard
                    label="Certifications"
                    value={stats.certifications}
                  />
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <ContactProCard
            email={
              displayValue(profile.email) === "-"
                ? undefined
                : String(profile.email)
            }
            phone={
              displayValue(profile.phone) === "-"
                ? undefined
                : String(profile.phone)
            }
            website={
              displayValue(profile.website) === "-"
                ? undefined
                : String(profile.website)
            }
          />

          <Card className="border-0" style={shellCardStyle}>
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6
                  className="mb-0"
                  style={{
                    color: COLORS.textPrimary,
                    fontWeight: 600,
                    fontSize: 17,
                    fontFamily: FONT_STACK,
                  }}
                >
                  Profile Completion
                </h6>
                <span
                  style={{
                    background:
                      completion === 100 ? COLORS.successBg : COLORS.brandSoft,
                    color: completion === 100 ? COLORS.successText : "#b45309",
                    fontWeight: 800,
                    fontSize: 12,
                    padding: "5px 10px",
                    borderRadius: 999,
                    fontFamily: FONT_STACK,
                  }}
                >
                  {completion}%
                </span>
              </div>

              <div
                style={{
                  background: "#e2e8f0",
                  height: 8,
                  borderRadius: 999,
                  overflow: "hidden",
                  marginBottom: 18,
                }}
              >
                <div
                  style={{
                    width: `${completion}%`,
                    height: "100%",
                    background: progressVariantColor,
                    borderRadius: 999,
                    transition: "0.3s ease",
                  }}
                />
              </div>

              <ul
                className="text-start"
                style={{
                  listStyle: "none",
                  paddingLeft: 0,
                  marginBottom: 0,
                }}
              >
                {[
                  { ok: isCompanyCompleted, label: "Company information" },
                  { ok: hasProducts, label: "Products listed" },
                  { ok: hasMachines, label: "Machines added" },
                  { ok: hasCertifications, label: "Certifications added" },
                ].map((item) => (
                  <li
                    key={item.label}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      marginBottom: 12,
                      color: item.ok
                        ? COLORS.textPrimary
                        : COLORS.textSecondary,
                      fontSize: 14,
                      fontWeight: 600,
                      fontFamily: FONT_STACK,
                    }}
                  >
                    <span
                      style={{
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: item.ok ? "#dcfce7" : "#f1f5f9",
                        color: item.ok ? "#16a34a" : "#94a3b8",
                        fontSize: 12,
                        flexShrink: 0,
                      }}
                    >
                      {item.ok ? "✓" : "•"}
                    </span>
                    {item.label}
                  </li>
                ))}
              </ul>
            </Card.Body>
          </Card>
        </Col>

        <Col md={9}>
          <Card className="border-0 mb-4" style={shellCardStyle}>
            <Card.Body className="p-2 p-lg-3">
              <Nav className="profile-top-tabs flex-wrap">
                <Nav.Item>
                  <Nav.Link
                    active={activeTab === "overview"}
                    onClick={() => setActiveTab("overview")}
                  >
                    Overview
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link
                    active={activeTab === "company"}
                    onClick={() => setActiveTab("company")}
                  >
                    Company
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link
                    active={activeTab === "products"}
                    onClick={() => setActiveTab("products")}
                  >
                    Products ({stats.products})
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link
                    active={activeTab === "machines"}
                    onClick={() => setActiveTab("machines")}
                  >
                    Machines ({stats.machines})
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link
                    active={activeTab === "certifications"}
                    onClick={() => setActiveTab("certifications")}
                  >
                    Certifications ({stats.certifications})
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link
                    active={activeTab === "documents"}
                    onClick={() => setActiveTab("documents")}
                  >
                    Documents ({documents.length})
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link
                    active={activeTab === "activity"}
                    onClick={() => setActiveTab("activity")}
                  >
                    Activity
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </Card.Body>
          </Card>

          {renderTabContent()}
        </Col>
      </Row>

      <style>
        {`
          .profile-top-tabs {
            gap: 10px;
          }

          .profile-top-tabs .nav-link {
            border: none !important;
            border-radius: 12px !important;
            color: #334155 !important;
            font-weight: 700;
            padding: 11px 18px;
            background: transparent;
            transition: 0.2s ease;
            cursor: pointer;
            font-family: ${FONT_STACK};
          }

         .profile-top-tabs .nav-link:hover {
  background: #facc15 !important;
  color: #111827 !important;
}

.profile-top-tabs .nav-link.active {
  background: #0f172a !important;
  color: #ffffff !important;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.18);
}

          .table thead th {
            background: #ffffff;
            color: #475569;
            font-size: 13px;
            font-weight: 700;
            border-bottom: 1px solid #cfd8e3 !important;
            padding-top: 14px !important;
            padding-bottom: 14px !important;
            font-family: ${FONT_STACK};
          }

          .table tbody td {
            vertical-align: middle;
            color: #0f172a;
            padding-top: 14px !important;
            padding-bottom: 14px !important;
            font-family: ${FONT_STACK};
          }

          .table-hover tbody tr:hover {
            background: #fcfdff;
          }

          .form-control,
          .form-select {
            border: 1px solid #cfd8e3 !important;
            box-shadow: none !important;
            background: #ffffff !important;
            font-family: ${FONT_STACK} !important;
          }

          .form-control:focus,
          .form-select:focus {
            border-color: #facc15 !important;
            box-shadow: 0 0 0 0.2rem rgba(250, 204, 21, 0.15) !important;
          }

          .form-label,
          .text-muted,
          .btn,
          .nav-link,
          .card,
          .badge {
            font-family: ${FONT_STACK} !important;
          }
        `}
      </style>
    </div>
  );
}
